/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    INVENTORY_MOBILE_CARD
 * Date:         2026-02-10
 * Modified By:  Claude
 *
 * Description:
 * Mobile inventory card component matching Maya's approved mockup.
 * Layout: Name + Price (top), Category + Qty + Condition (meta),
 * Description (2-line clamp), Time + Actions (footer).
 *
 * File Size Check:
 * - Current: 0 (new file)
 * - Adding: ~120 lines
 * - Projected: 120 lines
 * - Decision: Proceed (within component limit)
 *
 * Related Files:
 * - client/src/styles/components/_inventory-responsive.scss
 * - client/src/features/inventory/InventoryPage.tsx
 */

import React from "react";
import { Inventory } from "../../../interfaces/Inventory";
import { formatRelativeTimeCompact } from "../../dashboard/utils/broadcastHelpers";
// [2026-02-13] CURRENCY_PHASE2: Centralized single-price display
import { formatCurrency } from "../../../utils/priceDisplay";

interface MobileInventoryCardProps {
  item: Inventory;
  onEdit?: (item: Inventory) => void;
  onDelete?: (item: Inventory) => void;
  onClick?: (item: Inventory) => void;
  isDeleting?: boolean;
  showSellerInfo?: boolean;
}

// Get condition class modifier for chip styling
const getConditionClass = (condition?: string): string => {
  const c = (condition || "").toLowerCase().trim();
  if (c.includes("new retail")) return "new-retail";
  if (c.includes("new bulk")) return "new-bulk";
  if (c.includes("refurbished")) return "refurb";
  if (c.includes("used")) return "used";
  if (c.includes("as-is") || c.includes("as is")) return "as-is";
  return "neutral";
};

// Extract seller company name from item
const getSellerCompanyName = (item: Inventory): string | undefined => {
  if (item.seller && typeof item.seller === "object" && item.seller.companyName) {
    return item.seller.companyName;
  }
  if (
    item.sellerId &&
    typeof item.sellerId === "object" &&
    (item.sellerId as any).companyName
  ) {
    return (item.sellerId as any).companyName;
  }
  return undefined;
};

export const MobileInventoryCard: React.FC<MobileInventoryCardProps> = ({
  item,
  onEdit,
  onDelete,
  onClick,
  isDeleting = false,
  showSellerInfo = false,
}) => {
  const conditionClass = getConditionClass(item.condition);
  const sellerName = showSellerInfo ? getSellerCompanyName(item) : undefined;

  // [2026-02-13] CURRENCY_PHASE2: Single price in user's currency
  const priceDisplay = formatCurrency(item.price, item.currency, item.convertedPrice, item.displayCurrency);

  // Build item name from manufacturer + model or partNumber
  const itemName = `${item.manufacturer || ""} ${item.model || item.partNumber || ""}`.trim();

  // Format time display
  const timeDisplay = formatRelativeTimeCompact(item.createdAt);
  const footerTime = sellerName ? `${sellerName} · ${timeDisplay}` : timeDisplay;

  const handleCardClick = () => {
    if (onClick) onClick(item);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) onEdit(item);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) onDelete(item);
  };

  return (
    <div
      className="inventory-mobile-card"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => e.key === "Enter" && handleCardClick()}
    >
      {/* Top row: Name + Price */}
      <div className="inventory-mobile-card__top">
        <span className="inventory-mobile-card__name">{itemName || "—"}</span>
        <span className="inventory-mobile-card__price">{priceDisplay || "—"}</span>
      </div>

      {/* Meta row: Category chip + Qty + Condition chip */}
      <div className="inventory-mobile-card__meta">
        {item.category && (
          <span className="inventory-mobile-card__chip inventory-mobile-card__chip--cat">
            {item.category}
          </span>
        )}
        <span className="inventory-mobile-card__qty">Qty: {item.quantity ?? "—"}</span>
        {item.condition && (
          <span
            className={`inventory-mobile-card__chip inventory-mobile-card__chip--${conditionClass}`}
          >
            {item.condition}
          </span>
        )}
      </div>

      {/* Description (optional, 2-line clamp) */}
      {item.description && (
        <div className="inventory-mobile-card__desc">{item.description}</div>
      )}

      {/* Footer: Time (left) + Actions (right) */}
      <div className="inventory-mobile-card__footer">
        <span className="inventory-mobile-card__time">{footerTime}</span>
        {!showSellerInfo && (onEdit || onDelete) && (
          <div className={`inventory-mobile-card__actions ${isDeleting ? "disabled" : ""}`}>
            {onEdit && (
              <button
                className="inventory-mobile-card__btn inventory-mobile-card__btn--edit"
                onClick={handleEdit}
                disabled={isDeleting}
                aria-label="Edit item"
              >
                Edit
              </button>
            )}
            {onDelete && (
              <button
                className="inventory-mobile-card__btn inventory-mobile-card__btn--delete"
                onClick={handleDelete}
                disabled={isDeleting}
                aria-label="Delete item"
              >
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileInventoryCard;
